import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { RosterResponse, RosterUser, Session } from '@pace/contracts';
import { HOME_PATH, ROLES, type Role } from '@pace/domain';
import { LANGUAGE_NAMES, LANGUAGES } from '@pace/i18n';
import { Avatar, Button, IconButton, Logo, PinDots, PinPad, Spinner, useHistoryLayer } from '@pace/ui';
import { ROLE_COLOUR } from '../data/bootstrap';
import { useSetupStatus } from '../data/setup';
import { useI18n } from '../i18n';
import { api, ApiError } from '../lib/api';
import { IS_DEMO_PATH } from '../lib/base';
import { rememberedUser, rememberUser } from '../lib/storage';
import { useSession } from '../session';

function LangSwitch({ onDark }: { onDark?: boolean }) {
  const { lang, setLang } = useI18n();
  return (
    <div className={`seg${onDark ? ' seg--on-accent' : ''}`} role="group" aria-label="Language / भाषा">
      {LANGUAGES.map((l) => (
        <button key={l} type="button" aria-pressed={lang === l} onClick={() => setLang(l)}>
          {LANGUAGE_NAMES[l]}
        </button>
      ))}
    </div>
  );
}

export function AuthFrame({
  children,
  sub,
  pin,
}: {
  children: React.ReactNode;
  sub?: React.ReactNode;
  /** Full-screen PIN step (phones hide the hero, as in the prototype). */
  pin?: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className={`auth${pin ? ' auth--pin' : ''}`}>
      <header className="auth__hero">
        <div className="auth__hero-top">
          <span className="auth__logo">
            <Logo size={28} />
          </span>
          <LangSwitch onDark />
        </div>
        <h1 className="display">{t('app.name')}</h1>
        <p>{sub ?? t('app.product')}</p>
        <p className="auth__tag">
          {t('app.product')} — {t('app.tagline')}
        </p>
      </header>
      <main className="auth__body" id="main">
        {children}
      </main>
    </div>
  );
}

function PinStep({
  user,
  onBack,
  onDone,
}: {
  user: Pick<RosterUser, 'id' | 'name' | 'title' | 'photoUrl' | 'roles'>;
  onBack: () => void;
  onDone: (s: Session) => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (p: string) => {
      setBusy(true);
      try {
        onDone(await api<Session>('/auth/login', { body: { userId: user.id, pin: p } }));
      } catch (e) {
        setShake(true);
        setTimeout(() => setShake(false), 400);
        setPin('');
        if (e instanceof ApiError) {
          if (e.code === 'no_pin') {
            void navigate('/setup');
            return;
          }
          if (e.code === 'locked') {
            const until = Number(e.body.until);
            setError(
              t('login.locked', {
                time: new Date(until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              }),
            );
          } else if (e.code === 'invalid_pin') {
            const left = Number(e.body.attemptsLeft);
            setError(Number.isFinite(left) ? t('login.wrongPin', { n: left }) : t('login.wrongPinShort'));
          } else setError(t('login.loginFailed'));
        } else setError(t('login.loginFailed'));
      } finally {
        setBusy(false);
      }
    },
    [user.id, onDone, navigate, t],
  );

  const digit = (d: string) => {
    if (busy) return;
    setError(null);
    setPin((p) => {
      const next = (p + d).slice(0, 4);
      if (next.length === 4) void submit(next);
      return next;
    });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) digit(e.key);
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Escape') onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="pinstep">
      <div className="pinstep__top">
        <IconButton icon="back" label={t('common.back')} onClick={onBack} />
        <LangSwitch />
      </div>
      <div className="pinstep__who">
        <Avatar
          name={user.name}
          photoUrl={user.photoUrl}
          colour={ROLE_COLOUR[(user.roles[0] as Role) ?? 'admin']}
          size="xl"
        />
        <b>{user.name}</b>
        <span className="muted">{user.title}</span>
      </div>
      <p className="pinstep__label">{t('login.enterPin')}</p>
      <PinDots length={4} filled={pin.length} error={shake} />
      <p className={`pinstep__err${error ? ' is-err' : ''}`} role="alert">
        {error ?? ' '}
      </p>
      <PinPad
        onDigit={digit}
        onDelete={() => setPin((p) => p.slice(0, -1))}
        deleteLabel={t('a11y.delete')}
        disabled={busy}
        left={
          <button type="button" className="ghost" onClick={onBack}>
            {t('login.notYou')}
          </button>
        }
      />
    </div>
  );
}

/** Demo: banner with reset + way back to the product. Product: a link to try the demo. */
export function DemoLinks({ demo }: { demo: boolean }) {
  const { t } = useI18n();
  const status = useSetupStatus();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  if (demo) {
    return (
      <div className="banner banner--wait demo-bar">
        <span>{t('demo.banner')}</span>
        <span className="demo-bar__actions">
          {IS_DEMO_PATH && (
            <button
              type="button"
              className="btn btn--link"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api('/demo/reset', { body: {} });
                  rememberUser(null);
                  await qc.invalidateQueries();
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t('demo.reset')}
            </button>
          )}
          <a className="btn btn--link" href="/">
            {t('demo.realApp')}
          </a>
        </span>
      </div>
    );
  }
  if (!status.data?.demoUrl) return null;
  return (
    <p className="auth__demo">
      <a className="btn btn--link" href={status.data.demoUrl}>
        {t('demo.tryIt')}
      </a>
    </p>
  );
}

/**
 * Daily login (PRD): tap your face, enter your 4-digit PIN. On a personal device the
 * last user goes straight to the PIN pad; a paired shared department device shows only
 * that department's faces. /login/:role bookmarks open straight to that role's faces.
 */
export default function LoginPage() {
  const { t } = useI18n();
  const { session, signedIn } = useSession();
  const { role } = useParams();
  const navigate = useNavigate();
  const filterRole = role && (ROLES as readonly string[]).includes(role) ? (role as Role) : undefined;
  const [picked, setPicked] = useState<RosterUser | null>(null);
  // The PIN step is a layer over the name list: Back returns to the list.
  useHistoryLayer(!!picked, () => setPicked(null));
  const [params] = useSearchParams();
  const rawNext = params.get('next');
  // Only same-app paths (never another site).
  const next = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : null;
  // Personal devices go straight to the last user's PIN pad; the demo always shows the names.
  const [remembered, setRemembered] = useState(() => (IS_DEMO_PATH ? null : rememberedUser()));

  const roster = useQuery({
    queryKey: ['roster', filterRole ?? 'all'],
    queryFn: () => api<RosterResponse>(`/auth/roster${filterRole ? `?role=${filterRole}` : ''}`),
    staleTime: 60_000,
  });

  if (session) return <Navigate to={next ?? HOME_PATH[session.role]} replace />;

  const done = (s: Session) => {
    signedIn(s);
    const home = filterRole && s.user.roles.includes(filterRole) ? HOME_PATH[filterRole] : HOME_PATH[s.role];
    void navigate(next ?? home, { replace: true });
  };

  const device = roster.data?.device;
  const current = picked ?? (device?.kind !== 'shared' && remembered ? remembered : null);
  if (current) {
    return (
      <AuthFrame sub={roster.data?.plantName} pin>
        <PinStep
          user={current as RosterUser}
          onBack={() => {
            setPicked(null);
            if (!picked) {
              rememberUser(null);
              setRemembered(null);
            }
          }}
          onDone={done}
        />
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      sub={
        device?.kind === 'shared'
          ? t('login.sharedDevice', {
              label: device.label ?? '',
              role: device.role ? t(`roles.${device.role}`) : '',
            })
          : roster.data?.plantName
      }
    >
      <p className="eyebrow">{t('login.tapYourName')}</p>
      {roster.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : roster.isError ? (
        <div className="empty">
          <b>{t('login.loginFailed')}</b>
          <Button onClick={() => void roster.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : (
        <div className="faces">
          {roster.data.users.map((u) => (
            <button
              key={u.id}
              type="button"
              className="face"
              onClick={() => (u.hasPin ? setPicked(u) : void navigate('/setup'))}
            >
              <Avatar name={u.name} photoUrl={u.photoUrl} colour={ROLE_COLOUR[u.roles[0] ?? 'admin']} />
              <span>
                <span className="face__name">{u.name}</span>
                <span className="face__role">
                  {u.roles.map((r) => t(`roles.${r}`)).join(' + ')} · {u.title}
                </span>
                {!u.hasPin && <span className="face__role face__setup">{t('login.noPin')}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="auth__foot">
        <Link to="/setup" className="btn btn--link">
          {t('login.firstTime')}
        </Link>
        {device?.kind !== 'shared' && (
          <Link to="/pair" className="btn btn--link">
            {t('login.pairTitle')}
          </Link>
        )}
      </div>
      <DemoLinks demo={!!roster.data?.demo} />
    </AuthFrame>
  );
}
